import type { MomentsVectorSpace, ImageMoments} from "./moments";
import { getVectorSpaceForMoments } from "./moments";
import { pointDeviation, ordinaryLeastSquares } from "../ordinary-least-squares";
import { PI_2 } from "../constants";
import { swapArray } from "common/Support";

// This is approximately two degrees.
const zeroAngleDelta = 0.035;

export type AngleClusterForMomentsParameters = Readonly<{
    minMomentSize: number;
    minMomentQuotient: number;
    minClusterCount: number;
    minClusterDividend: number;
    maxDelta: number;
    maxDistanceFactor: number;
    maxDeviationTolerance: number;
}>;

interface ProposedAngleCluster {
    totalRadians: number;
    totalCount: number;
    merged: boolean;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    vectorSpaces: MomentsVectorSpace[];
}

function angleForCluster(cluster: ProposedAngleCluster): number {
    return cluster.totalRadians / cluster.totalCount;
}

function mergeClusters(dst: ProposedAngleCluster, src: ProposedAngleCluster) {
    const srcAngle = angleForCluster(src);
    const dstAngle = angleForCluster(dst);

    const dstZero = dstAngle > Math.PI - zeroAngleDelta;
    const srcZero = srcAngle > Math.PI - zeroAngleDelta;

    let updated = dstZero ? 0 : dst.totalRadians;

    if (dstZero === srcZero) {
        updated += srcZero ? 0 : src.totalRadians;
    } else if (dstZero) {
        updated = src.totalRadians + (srcAngle <= PI_2 ? 0 : Math.PI * dst.totalCount);
    } else {
        // !dstZero, srcZero
        updated = dst.totalRadians + (dstAngle <= PI_2 ? 0 : Math.PI * src.totalCount);
    }

    dst.totalCount += src.totalCount;
    dst.totalRadians = updated;
    dst.vectorSpaces.push(...src.vectorSpaces);

    if (src.minX < dst.minX) {
        dst.minX = src.minX;
    }
    if (src.maxX > dst.maxX) {
        dst.maxX = src.maxX;
    }
    if (src.minY < dst.minY) {
        dst.minY = src.minY;
    }
    if (src.maxY > dst.maxY) {
        dst.maxY = src.maxY;
    }

    src.merged = true;
}

function sortAngleCluster(left: ProposedAngleCluster, right: ProposedAngleCluster): number {
    if (right.merged === true && left.merged === true) {
        // We don't care about merged angles, so left can stay where it is.
        return -1;
    } else if (left.merged === true) {
        // Right was not merged but left was, so right is actually less than left.
        return 1;
    } else if (right.merged === true) {
        // Left was not merged but right was, so left is less than right.
        return -1;
    }

    return angleForCluster(left) - angleForCluster(right);
}

export function lineLengthOnVectorSpace(vs: MomentsVectorSpace, lineTheta: number): number {
    if (vs.theta === undefined) return 0;
    const vsHyp = Math.abs(vs.minorLength / Math.cos(vs.theta - PI_2 - lineTheta));
    return Math.min(isNaN(vsHyp) ? Number.MAX_SAFE_INTEGER : vsHyp, vs.majorLength) / 2;
}

export interface DistanceStatistics {
    min: number;
    max: number;
    average: number;
}

function xDistanceStatistics(proposedCluster: ProposedAngleCluster): DistanceStatistics {
    const { vectorSpaces } = proposedCluster;
    if (vectorSpaces.length < 2) {
        return {
            average: 0,
            min: 0,
            max: 0,
        };
    }

    const slopeIntercept = ordinaryLeastSquares(vectorSpaces, v => [v.centerX, v.centerY]);
    const m = slopeIntercept[0];
    let b = slopeIntercept[1];
    const hyp = Math.sqrt(1 + m * m);

    let s = m / hyp;
    let c = 1 / hyp;
    let olsTheta = Math.atan(s / c);

    if (isNaN(hyp)) {
        s = 1;
        c = 0;
        olsTheta = PI_2;
        b = vectorSpaces[0].centerX;
    }

    // As in X, thickness
    const xts: [number, number][] = [];
    for (const vs of vectorSpaces) {
        // + sin() because we're trying to rotate in the negative angle direction
        const xP = c * vs.centerX + s * (vs.centerY - b);
        xts.push([xP, lineLengthOnVectorSpace(vs, olsTheta)]);
    }

    let totalDistance = 0;
    let min = Number.MAX_SAFE_INTEGER;
    let max = Number.MIN_SAFE_INTEGER;
    xts.sort((l, r) => l[0] - r[0]);
    for (let i = 0; i < xts.length - 1; i++) {
        const right = xts[i + 1];
        const left = xts[i];
        const dist = Math.max(right[0] - left[0] - (right[1] + left[1]), 0);
        totalDistance += dist;
        if (dist < min) {
            min = dist;
        }
        if (dist > max) {
            max = dist;
        }
    }

    return {
        average: xts.length < 2 ? 0 : totalDistance / (xts.length - 1),
        min,
        max,
    };
}

export type AngleCluster = Readonly<{
    theta: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    distanceStatistics: DistanceStatistics;
    lineCount: number;
    vectorSpaces: MomentsVectorSpace[];
}> & { label: number };

export function angleClusterForMoments(
    moments: readonly ImageMoments[],
    {
        minMomentSize,
        minMomentQuotient,
        minClusterCount,
        minClusterDividend,
        maxDelta,
        maxDistanceFactor,
        maxDeviationTolerance,
    }: AngleClusterForMomentsParameters
): AngleCluster | undefined {
    const clusters: ProposedAngleCluster[] = [];
    let consideredMinX: number = Number.MAX_SAFE_INTEGER;
    let consideredMaxX: number = 0;
    let consideredMinY: number = Number.MAX_SAFE_INTEGER;
    let consideredMaxY: number = 0;

    for (const moment of moments) {
        const vectorSpace = getVectorSpaceForMoments(moment);
        const { theta, eccentricity } = vectorSpace;
        if (theta === undefined) continue;

        // If we don't have at least a 2x ratio of primary eigenvector length
        // to secondary eigenvector length, then we're dealing with a near-square
        // image, and that's ultimately not going to be a line.
        //
        // The eccentricity is sqrt(1 - l2 / l1), and we're checking that l2 / l1
        // < 0.5.
        if (eccentricity < 0.7) continue;
        if (moment.raw00 * eccentricity < minMomentSize) continue;

        clusters.push({
            totalRadians: theta,
            totalCount: 1,
            merged: false,
            minX: moment.minX,
            maxX: moment.maxX,
            minY: moment.minY,
            maxY: moment.maxY,
            vectorSpaces: [vectorSpace],
        });

        if (moment.minX < consideredMinX) {
            consideredMinX = moment.minX;
        }
        if (moment.maxX > consideredMaxX) {
            consideredMaxX = moment.maxX;
        }
        if (moment.minY < consideredMinY) {
            consideredMinY = moment.minY;
        }
        if (moment.maxY > consideredMaxY) {
            consideredMaxY = moment.maxY;
        }
    }

    if (clusters.length < minClusterCount) {
        return undefined;
    }

    if (clusters.length < (moments.length * minMomentQuotient) / minClusterDividend) {
        return undefined;
    }

    // Skip chunks where the standard deviation from the OLS line is too
    // great; this helps avoid treating text-between-barcodes as a barcode,
    // and will prevent us from unifying multiple barcodes into one "big"
    // barcode during location.
    const deviation = pointDeviation(
        clusters.map(c => c.vectorSpaces[0]),
        v => [v.centerX, v.centerY]
    );
    const deviationThreshold = Math.max(consideredMaxX - consideredMinX, consideredMaxY - consideredMinY);

    if (deviation > deviationThreshold * maxDeviationTolerance) {
        return undefined;
    }

    let clusterCount = clusters.length;
    do {
        if (clusterCount <= 1) break;

        clusters.sort(sortAngleCluster);
        let mergeChoice: number | undefined;
        let mergeDelta: number = 0;

        for (let i = 0; i < clusterCount - 1; i++) {
            const delta = angleForCluster(clusters[i + 1]) - angleForCluster(clusters[i]);
            if (delta > maxDelta) continue;

            if (mergeChoice !== undefined && delta > mergeDelta) continue;
            mergeChoice = i;
            mergeDelta = delta;
        }

        const lastAngleForCluster = angleForCluster(clusters[clusterCount - 1]);

        // If this is a negative angle in the second quadrant, but is close enough
        // to PI, then we'll just treat it like 0.
        if (lastAngleForCluster > Math.PI - zeroAngleDelta) {
            const delta = angleForCluster(clusters[0]) + lastAngleForCluster - Math.PI;
            if (delta <= maxDelta && (mergeChoice === undefined || delta <= mergeDelta)) {
                mergeClusters(clusters[0], clusters[clusterCount - 1]);
                clusterCount--;
                continue;
            }
        }
        if (mergeChoice !== undefined) {
            mergeClusters(clusters[mergeChoice + 1], clusters[mergeChoice]);
            swapArray(clusters, mergeChoice, clusterCount - 1);
            clusterCount--;
            continue;
        }
        break;
    } while (true);

    let bestCount = 0;
    let bestIndex = -1;

    for (let i = 0; i < clusterCount; i++) {
        const count = clusters[i].totalCount;
        if (count > bestCount) {
            bestIndex = i;
            bestCount = count;
        }
    }

    if (bestCount > 0) {
        const bestProposed = clusters[bestIndex];
        if (bestProposed === undefined) return undefined;

        const { totalCount, minX, minY, maxX, maxY } = bestProposed;

        let theta = angleForCluster(bestProposed);
        theta = theta > Math.PI - zeroAngleDelta ? 0 : theta;
        const distanceStatistics = xDistanceStatistics(bestProposed);

        // Before we accept this angle cluster, we need to ensure that the
        // average distance between components is within the tolerance of
        // maxDistanceFactor * boundingDistance, where boundingDistance is the
        // length of the hypotenuse that would fit in the bounding box assuming
        // an angle from the X axis based on the tangent of the angle of the
        // components.
        if (bestProposed.vectorSpaces.length > 1) {
            const maxDistanceX = (maxX - minX) * Math.cos(theta + PI_2);
            const maxDistanceY = (maxY - minY) * Math.sin(theta + PI_2);
            const maxDistance =
                Math.sqrt(maxDistanceX * maxDistanceX + maxDistanceY * maxDistanceY) * maxDistanceFactor;
            if (distanceStatistics.max > maxDistance) {
                return undefined;
            }
        }

        return {
            theta,
            distanceStatistics,
            minX,
            minY,
            maxX,
            maxY,
            label: 0,
            lineCount: totalCount,
            vectorSpaces: bestProposed.vectorSpaces,
        };
    } else {
        return undefined;
    }
}
